import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  SelectChangeEvent,
  Button,
  InputAdornment,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Driver } from '../types/models';
import { RootState } from '../types/store';
import { formatPhoneNumber, formatPhoneNumberOrText, isPhoneNumber, normalizePhoneNumber } from '../utils/phoneNumberUtils';
import { setFilters, setSort, fetchDriversRealtime } from '../store/slices/driverSlice';
import type { AppDispatch } from '../store';
import SearchIcon from '@mui/icons-material/Search';
import ExtensionIcon from '@mui/icons-material/Extension';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ref, get, set, update } from 'firebase/database';
import { database, DB_PATHS } from '../config/firebase';
import dayjs from 'dayjs';
import ChungcheongExtensionRequest from '../components/ChungcheongExtensionRequest';
import { getDisplayAppName } from '../utils/appNameUtils';
import GroupIcon from '@mui/icons-material/Group';

const DriversPage = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const driverState = useSelector((state: RootState) => state.driver);
  const authUser = useSelector((state: RootState) => state.auth.user);
  const userGrade = authUser?.grade;
  const userAppname = authUser?.appname;
  const userReferralcode = authUser?.referralcode;
  const isLocalManager = userGrade === 'localmanager' || userGrade === 'localmaster';
  const { items: drivers, filters, sort, loading, error } = driverState || { items: [], filters: {}, sort: 'name', loading: false, error: null };

  const [searchQuery, setSearchQuery] = useState('');
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [driversWithExtensionRequest, setDriversWithExtensionRequest] = useState<Set<string>>(new Set());
  const [insufficientDepositDialogOpen, setInsufficientDepositDialogOpen] = useState(false);
  
  // 충청 앱 연장요청 관련 상태
  const [chungcheongExtensionDialogOpen, setChungcheongExtensionDialogOpen] = useState(false);
  
  // 연장요청 필터 상태
  const [showOnlyExtensionRequests, setShowOnlyExtensionRequests] = useState(false);
  
  // 요청취소 관련 상태
  const [cancelRequestDialogOpen, setCancelRequestDialogOpen] = useState(false);
  const [driverToCancel, setDriverToCancel] = useState<Driver | null>(null);
  const [noPermissionDialogOpen, setNoPermissionDialogOpen] = useState(false);
  
  // 연장승인 안내 다이얼로그 상태 (localmanager 오토콜 전용)
  const [extensionGuideDialogOpen, setExtensionGuideDialogOpen] = useState(false);
  
  // 추가폰 그룹 정보 상태
  const [phoneGroups, setPhoneGroups] = useState<Map<string, number>>(new Map());

  // 연장요청 권한 체크: supermaster 또는 appname이 "smlink_M2_오토콜"인 localmaster
  const canProcessExtensionRequest = userGrade === 'supermaster' || (isLocalManager && userAppname === 'smlink_M2_오토콜') || (isLocalManager && userAppname === 'smlink_M2_이지캐치') || (userAppname === 'smlink_M2_충청');
  
  // 요청취소 권한 체크: supermaster 또는 appname이 "smlink_M2_오토콜" 또는 "smlink_M2_충청"인 localmaster/localmanager만
  const canCancelRequest = userGrade === 'supermaster' || (isLocalManager && (userAppname === 'smlink_M2_오토콜' || userAppname === 'smlink_M2_충청'));

    // 임시 데이터 조작 함수 (실행 후 삭제)
  // const tempDataManipulation = async () => {
  //   try {
  //     // 1. depositlogs 컬렉션에서 "01074444944" 데이터 중 type이 "withdraw"인 데이터 조회
  //     const depositLogsRef = ref(database, 'depositlogs/01074444944');
  //     const depositLogsSnapshot = await get(depositLogsRef);
      
  //     if (depositLogsSnapshot.exists()) {
  //       const depositLogsData = depositLogsSnapshot.val();
        
  //       // type이 "withdraw"인 데이터 필터링
  //       const withdrawLogs = Object.entries(depositLogsData)
  //         .filter(([timestamp, logData]: [string, any]) => logData.type === 'withdraw')
  //         .map(([timestamp, logData]: [string, any]) => ({ timestamp, ...logData }));
        
  //       console.log('withdraw 로그들:', withdrawLogs);
        
  //       // 2. reason 필드에서 괄호 사이의 전화번호 추출
  //       const logsWithPhoneNumbers = withdrawLogs
  //         .map(log => {
  //           const reason = log.reason || '';
  //           const match = reason.match(/\(([^)]+)\)/);
  //           const phoneNumber = match ? match[1] : null;
  //           return { ...log, extractedPhoneNumber: phoneNumber };
  //         })
  //         .filter(log => log.extractedPhoneNumber !== null);
        
  //       console.log('전화번호가 추출된 로그들:', logsWithPhoneNumbers);
        
  //       // 3. 각 로그별로 전화번호로 driversm2 컬렉션에서 기사 조회
  //       for (const log of logsWithPhoneNumbers) {
  //         const phoneNumber = log.extractedPhoneNumber;
  //         const driversRef = ref(database, 'driversm2');
  //         const driversSnapshot = await get(driversRef);
          
  //         if (driversSnapshot.exists()) {
  //           const driversData = driversSnapshot.val();
            
  //           // 전화번호로 기사 찾기
  //           const driver = Object.values(driversData).find((driver: any) => 
  //             driver.phonenumber === phoneNumber
  //           );
            
  //           if (driver) {
  //             // 4. referealcode 필드 값 조회
  //             const referealcode = (driver as any).referealcode;
  //             console.log(`전화번호 ${phoneNumber}의 referealcode:`, referealcode);
              
  //             // 5. referealcode가 일치하는 로그만 필터링하여 저장
  //             if (referealcode) {
  //               // 현재 로그의 reason에서 추출된 전화번호로 찾은 referealcode와 일치하는 로그들만 필터링
  //               const matchingLogs = logsWithPhoneNumbers.filter(logItem => {
  //                 const logReason = logItem.reason || '';
  //                 const logMatch = logReason.match(/\(([^)]+)\)/);
  //                 const logPhoneNumber = logMatch ? logMatch[1] : null;
                  
  //                 if (logPhoneNumber) {
  //                   // 해당 전화번호로 기사를 찾아서 referealcode 확인
  //                   const logDriver = Object.values(driversData).find((driver: any) => 
  //                     driver.phonenumber === logPhoneNumber
  //                   );
  //                   return logDriver && (logDriver as any).referealcode === referealcode;
  //                 }
  //                 return false;
  //               });
                
  //                                // 필터링된 로그들을 depositLogsData 형태로 변환
  //                const filteredDepositLogsData: any = {};
  //                matchingLogs.forEach(logItem => {
  //                  filteredDepositLogsData[logItem.timestamp] = {
  //                    type: logItem.type,
  //                    amount: logItem.amount,
  //                    date: logItem.date,
  //                    reason: logItem.reason,
  //                    balance: logItem.balance
  //                  };
  //                });
                
  //               // 6. referealcode를 key로 하여 필터링된 데이터만 저장
  //               const referealcodeLogsRef = ref(database, `depositlogs/${referealcode}`);
  //               await set(referealcodeLogsRef, filteredDepositLogsData);
  //               console.log(`referealcode ${referealcode}에 필터링된 데이터 저장 완료:`, Object.keys(filteredDepositLogsData).length, '개 로그');
  //             }
  //           } else {
  //             console.log(`전화번호 ${phoneNumber}에 해당하는 기사를 찾을 수 없습니다.`);
  //           }
  //         }
  //       }
  //     } else {
  //       console.log('depositlogs/01074444944 데이터가 없습니다.');
  //     }
  //   } catch (error) {
  //     console.error('tempDataManipulation 실행 중 오류:', error);
  //   }
  // };

  // 현재 로그인한 관리자의 예치금 확인
  const checkManagerDeposit = async (): Promise<boolean> => {
    try {
      if (!authUser?.id) return false;
      
      const depositRef = ref(database, `deposit/${authUser.id}`);
      const snapshot = await get(depositRef);
      
      if (snapshot.exists()) {
        const depositData = snapshot.val();
        const currentDeposit = depositData.currentDeposit || 0;
        console.log('현재 관리자 예치금:', currentDeposit);
        return currentDeposit >= 20000;
      } else {
        console.log('예치금 데이터 없음');
        return false;
      }
    } catch (error) {
      console.error('예치금 확인 실패:', error);
      return false;
    }
  };

  // 실시간 데이터 동기화 구독
  useEffect(() => {
    const unsubscribe = dispatch(fetchDriversRealtime());
    
    return () => {
      // 구독 해제
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [dispatch]);

  // 데이터 로드 후 필터 적용
  useEffect(() => {
    // drivers 데이터가 로드된 후에만 필터 적용
    if (drivers.length > 0) {
      // localmaster라면 자신의 appname으로 지역 필터 강제 적용
      if (userGrade === 'localmaster' && userAppname) {
        dispatch(setFilters({ region: userAppname, referealcode: '' }));
      }
      // localmanager라면 자신의 appname, referealcode로 지역/추천인 필터 강제 적용
      if (userGrade === 'localmanager' && userAppname && userReferralcode) {
        dispatch(setFilters({ region: userAppname, referealcode: userReferralcode }));
      }
    }
  }, [dispatch, userGrade, userAppname, userReferralcode, drivers.length]);

  // 추가폰 그룹 정보를 먼저 로드 (phoneToGroup 테이블 직접 조회)
  useEffect(() => {
    const loadPhoneGroups = async () => {
      // SuperMaster만 추가폰 정보를 볼 수 있음
      if (userGrade !== 'supermaster') {
        return;
      }

      try {
        console.log('추가폰 그룹 정보 로드 시작 (phoneToGroup 테이블 직접 조회)');
        
        // phoneToGroup 테이블 전체를 먼저 가져옴
        const phoneToGroupRef = ref(database, 'phoneToGroup');
        const phoneToGroupSnapshot = await get(phoneToGroupRef);
        
        if (!phoneToGroupSnapshot.exists()) {
          console.log('phoneToGroup 데이터가 없습니다.');
          return;
        }

        // phoneToGroupData는 현재 사용하지 않지만 향후 확장을 위해 유지
        // const phoneToGroupData = phoneToGroupSnapshot.val();
        const phoneGroupMap = new Map<string, number>();
        
        // accountGroups 테이블 가져오기
        const accountGroupsRef = ref(database, 'accountGroups');
        const accountGroupsSnapshot = await get(accountGroupsRef);
        
        if (!accountGroupsSnapshot.exists()) {
          console.log('accountGroups 데이터가 없습니다.');
          return;
        }

        const accountGroupsData = accountGroupsSnapshot.val();
        
        // 각 그룹의 멤버 수 계산
        for (const [groupId, groupData] of Object.entries(accountGroupsData)) {
          const group = groupData as any;
          if (group.members && Array.isArray(group.members)) {
            // 그룹의 각 멤버에 대해 추가폰 수 설정
            for (const memberPhone of group.members) {
              // 자기 자신을 제외한 추가폰 수
              const additionalPhones = group.members.length - 1;
              if (additionalPhones > 0) {
                phoneGroupMap.set(memberPhone, additionalPhones);
                console.log(`${memberPhone}: 추가폰 ${additionalPhones}개 (그룹: ${groupId})`);
              }
            }
          }
        }
        
        console.log('추가폰 그룹 정보 로드 완료:', phoneGroupMap);
        setPhoneGroups(phoneGroupMap);
      } catch (error) {
        console.error('추가폰 그룹 정보 로드 실패:', error);
      }
    };

    // 컴포넌트 마운트 시 즉시 실행
    loadPhoneGroups();
  }, [userGrade]); // drivers 의존성 제거

  // 연장요청이 있는 기사들을 찾는 함수
  useEffect(() => {
    const checkExtensionRequests = async () => {
      if (!canProcessExtensionRequest) {
        return;
      }

      try {
        // autocalllogs 전체를 한 번에 가져오기
        const allLogsRef = ref(database, DB_PATHS.AUTOCALLLOGS);
        const snapshot = await get(allLogsRef);
        
        const requestDrivers = new Set<string>();
        
        if (snapshot.exists()) {
          const allLogs = snapshot.val();
          
          // 1. allLogs에서 changeType이 'request'인 드라이버 ID들을 먼저 추출
          const requestDriverIds = new Set<string>();
          
          Object.entries(allLogs).forEach(([driverId, driverLogs]) => {
            if (driverLogs && typeof driverLogs === 'object') {
              // 해당 드라이버의 로그를 timestamp 순으로 정렬하여 마지막 로그 찾기
              const sortedLogs = Object.entries(driverLogs as Record<string, any>).sort(([a], [b]) => b.localeCompare(a));
              
              if (sortedLogs.length > 0) {
                const [, lastLog] = sortedLogs[0];
                if (lastLog && typeof lastLog === 'object' && (lastLog as any).changeType === 'request') {
                  requestDriverIds.add(driverId);
                }
              }
            }
          });
          
          // 2. 추출된 드라이버 ID들이 현재 drivers 목록에 있는지 확인
          drivers.forEach(driver => {
            if (requestDriverIds.has(driver.id)) {
              requestDrivers.add(driver.id);
            }
          });
        }
        
        console.log('연장요청 기사 수:', requestDrivers.size);
        setDriversWithExtensionRequest(requestDrivers);
        
      } catch (error) {
        console.error('driverlogs 조회 실패:', error);
        setDriversWithExtensionRequest(new Set());
      }
    };

    checkExtensionRequests();
  }, [drivers, canProcessExtensionRequest]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    dispatch(setFilters({ ...filters, searchQuery: query }));
  };

  const handleSort = (sortType: 'name' | 'phone' | 'expire_asc' | 'expire_desc' | 'login_asc' | 'login_desc' | 'initdate_asc' | 'initdate_desc') => {
    dispatch(setSort(sortType));
  };

  const handleDriverClick = (driver: Driver) => {
    // 모든 관리자가 기사 상세정보를 볼 수 있도록 변경
    navigate(`/drivers/${driver.id}`);
  };

  const handleExtensionRequest = async (driver: Driver) => {
    // localmanager이면서 오토콜 앱인 경우 안내 다이얼로그만 표시
    if (userGrade === 'localmanager' && userAppname === 'smlink_M2_오토콜') {
      setSelectedDriver(driver);
      setExtensionGuideDialogOpen(true);
      return;
    }
    
    // 충청 앱인 경우 별도 컴포넌트 사용
    if (userAppname === 'smlink_M2_충청') {
      setSelectedDriver(driver);
      setChungcheongExtensionDialogOpen(true);
      return;
    }
    
    // 기존 로직 (다른 앱들)
    const hasEnoughDeposit = await checkManagerDeposit();
    
    if (!hasEnoughDeposit) {
      setInsufficientDepositDialogOpen(true);
      return;
    }
    
    // 예치금이 충분하면 정상 진행
    setSelectedDriver(driver);
    setExtensionDialogOpen(true);
  };

  // 요청취소 핸들러
  const handleCancelRequest = (driver: Driver) => {
    // supermaster인 경우 권한 없음 다이얼로그 표시
    if (userGrade === 'supermaster') {
      setNoPermissionDialogOpen(true);
      return;
    }
    
    setDriverToCancel(driver);
    setCancelRequestDialogOpen(true);
  };

  // 요청취소 확인 처리
  const handleCancelRequestConfirm = async () => {
    if (!driverToCancel) return;

    try {
      const now = new Date();
      const timestamp = now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0') +
        String(now.getSeconds()).padStart(2, '0');

      // 1. autocalllogs에 취소 로그 기록
      const cancelLogData = {
        driverId: driverToCancel.id,
        driverName: driverToCancel.username,
        driverPhone: driverToCancel.phonenumber,
        cancelledByName: authUser?.username || 'unknown',
        cancelledById: authUser?.id || 'unknown',
        cancelDate: now.toISOString(),
        changeType: 'request_cancelled'
      };

      const autocalllogRef = ref(database, `${DB_PATHS.AUTOCALLLOGS}/${driverToCancel.id}/${timestamp}`);
      await set(autocalllogRef, cancelLogData);

      // 2. depositlogs에 취소 기록 (localmaster, localmanager만)
      if (userGrade === 'localmaster' || userGrade === 'localmanager') {
        // localmaster는 본인 전화번호, localmanager는 추천인 코드로 기록
        const depositLogId = userGrade === 'localmaster' ? authUser?.id : userReferralcode;
        
        if (depositLogId) {
          // 현재 예치금 조회 (잔액 표시를 위해)
          const depositRef = ref(database, `deposit/${depositLogId}`);
          const depositSnapshot = await get(depositRef);
          let currentBalance = 0;
          
          if (depositSnapshot.exists()) {
            const depositData = depositSnapshot.val();
            currentBalance = depositData.currentDeposit || 0;
          }

          const depositLogData = {
            type: 'cancel',
            amount: 0,
            date: now.getFullYear() + '-' +
              String(now.getMonth() + 1).padStart(2, '0') + '-' +
              String(now.getDate()).padStart(2, '0') + ' ' +
              String(now.getHours()).padStart(2, '0') + ':' +
              String(now.getMinutes()).padStart(2, '0') + ':' +
              String(now.getSeconds()).padStart(2, '0'),
            reason: `연장요청 취소 - ${driverToCancel.username} (${driverToCancel.phonenumber})`,
            balance: currentBalance
          };

          const depositLogRef = ref(database, `depositlogs/${depositLogId}/${timestamp}`);
          await set(depositLogRef, depositLogData);
        }
      }

      // 3. 연장요청 목록에서 제거
      setDriversWithExtensionRequest(prev => {
        const newSet = new Set(prev);
        newSet.delete(driverToCancel.id);
        return newSet;
      });

      // 4. 다이얼로그 닫기 및 상태 초기화
      setCancelRequestDialogOpen(false);
      setDriverToCancel(null);
      alert('연장요청이 취소되었습니다.');
    } catch (error) {
      console.error('요청취소 처리 실패:', error);
      alert('요청취소 처리에 실패했습니다.');
    }
  };

  const handleExtensionApproval = async () => {
    if (!selectedDriver) return;

    try {
      // 더 안전한 만료일 계산 로직
      const today = dayjs().startOf('day');
      const currentExpireDate = dayjs(selectedDriver.expiredate).startOf('day');
      
      let baseDate;
      if (today.isAfter(currentExpireDate)) {
        // 만료일이 이미 지난 경우: 오늘 기준으로 1개월 연장
        baseDate = today;
        console.log('만료일이 이미 지남 - 오늘 기준으로 연장');
      } else {
        // 만료일이 아직 유효한 경우: 현재 만료일 기준으로 1개월 연장
        baseDate = currentExpireDate;
        console.log('만료일이 아직 유효함 - 현재 만료일 기준으로 연장');
      }
      
      const newExpireDate = baseDate.add(1, 'month').format('YYYY-MM-DD') + ' 12:00:00';

      // 기사 정보 업데이트
      const driverRef = ref(database, `${DB_PATHS.DRIVERS}/${selectedDriver.id}`);
      await update(driverRef, { expiredate: newExpireDate });

      // 로그 기록
      const now = new Date();
      const timestamp = now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0') +
        String(now.getSeconds()).padStart(2, '0');

      const logData = {
        driverId: selectedDriver.id,
        driverName: selectedDriver.username,
        oldExpireDate: selectedDriver.expiredate,
        newExpireDate: newExpireDate,
        changedByName: authUser?.username || 'unknown',
        changedById: authUser?.id || 'unknown',
        changeDate: now.toISOString(),
        changeType: 'request_expire_date_update'
      };

      const logRef = ref(database, `${DB_PATHS.DRIVERLOGS}/${selectedDriver.id}/${timestamp}`);
      await set(logRef, logData);
      const autocalllogRef = ref(database, `${DB_PATHS.AUTOCALLLOGS}/${selectedDriver.id}/${timestamp}`);
      await set(autocalllogRef, logData);

      // 관리자 예치금 차감 처리 (20,000원)
      const deductionAmount = 20000;
      if (authUser?.id) {
        try {
          // 현재 예치금 조회
          const depositRef = ref(database, `deposit/${authUser.id}`);
          const depositSnapshot = await get(depositRef);
          
          if (depositSnapshot.exists()) {
            const depositData = depositSnapshot.val();
            const currentDeposit = depositData.currentDeposit || 0;
            const newDeposit = currentDeposit - deductionAmount;

            // deposit 테이블 업데이트
            const depositUpdateData = {
              phonenumber: authUser.id,
              username: authUser.username || 'unknown',
              currentDeposit: newDeposit,
              lastUpdateDate: now.getFullYear() + '-' +
                String(now.getMonth() + 1).padStart(2, '0') + '-' +
                String(now.getDate()).padStart(2, '0') + ' ' +
                String(now.getHours()).padStart(2, '0') + ':' +
                String(now.getMinutes()).padStart(2, '0') + ':' +
                String(now.getSeconds()).padStart(2, '0')
            };

            await set(depositRef, depositUpdateData);

            // depositlogs 테이블에 출금 로그 추가
            const depositLogData = {
              type: 'withdraw',
              amount: deductionAmount,
              date: depositUpdateData.lastUpdateDate,
              reason: `연장요청 승인 - ${selectedDriver.username} (${selectedDriver.phonenumber})`,
              balance: newDeposit // 차감 후 잔액 추가
            };

            const depositLogRef = ref(database, `depositlogs/${authUser.id}/${timestamp}`);
            await set(depositLogRef, depositLogData);

            if (selectedDriver.referealcode && selectedDriver.referealcode !== '') {
              const managerDepositLogRef = ref(database, `depositlogs/${selectedDriver.referealcode}/${timestamp}`);
              await set(managerDepositLogRef, depositLogData);
              console.log(`manager로그: ${selectedDriver.referealcode}`);
            }

            console.log(`예치금 차감 완료: ${currentDeposit} → ${newDeposit}`);
          } else {
            console.error('예치금 데이터를 찾을 수 없습니다.');
          }
        } catch (error) {
          console.error('예치금 차감 처리 실패:', error);
        }
      }

      // 연장요청 목록에서 제거
      setDriversWithExtensionRequest(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedDriver.id);
        return newSet;
      });

      setExtensionDialogOpen(false);
      setSelectedDriver(null);
      alert('연장요청이 승인되었습니다.');
    } catch (error) {
      console.error('연장요청 승인 실패:', error);
      alert('연장요청 승인에 실패했습니다.');
    }
  };

  // 충청 연장요청 성공 시 콜백
  const handleChungcheongExtensionSuccess = () => {
    setDriversWithExtensionRequest(prev => {
      const newSet = new Set(prev);
      if (selectedDriver) {
        newSet.delete(selectedDriver.id);
      }
      return newSet;
    });
  };

  // 1. 지역 필터 적용
  const filteredDriversByRegion = drivers.filter(driver => {
    if (filters.region && driver.appname !== filters.region) {
      return false;
    }
    return true;
  });

  // 2. 추천인(referealcode) 옵션 추출 (지역 필터 적용된 목록에서만)
  const referealOptions = userGrade === 'localmanager' 
    ? [userReferralcode].filter(Boolean)
    : Array.from(
        new Set(filteredDriversByRegion.map(driver => driver.referealcode).filter(Boolean))
      ).sort((a, b) => {
        // 전화번호와 일반 텍스트를 구분하여 정렬
        const aIsPhone = isPhoneNumber(a);
        const bIsPhone = isPhoneNumber(b);
        
        if (aIsPhone && !bIsPhone) return -1;
        if (!aIsPhone && bIsPhone) return 1;
        
        return a.localeCompare(b);
      });

  // 3. 최종 드라이버 목록(추천인 등 다른 필터까지 적용)
  const filteredDrivers = filteredDriversByRegion.filter(driver => {
    if (filters.referealcode && filters.referealcode !== '') {
      // 전화번호 형식 정규화 후 비교
      const normalizedFilterCode = normalizePhoneNumber(filters.referealcode);
      const normalizedDriverCode = normalizePhoneNumber(driver.referealcode);
      
      if (normalizedDriverCode !== normalizedFilterCode) {
        return false;
      }
    }
    if (filters.searchQuery) {
      const q = filters.searchQuery.trim().toLowerCase();
      if (
        !driver.username.toLowerCase().includes(q) &&
        !driver.phonenumber.toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    // 연장요청 필터 적용
    if (showOnlyExtensionRequests && !driversWithExtensionRequest.has(driver.id)) {
      return false;
    }
    return true;
  }).sort((a, b) => {
    switch (sort) {
      case 'name':
        return a.username.localeCompare(b.username);
      case 'phone':
        return a.phonenumber.localeCompare(b.phonenumber);
      case 'expire_asc':
        return dayjs(a.expiredate).diff(dayjs(b.expiredate));
      case 'expire_desc':
        return dayjs(b.expiredate).diff(dayjs(a.expiredate));
      case 'login_asc':
        return dayjs(a.logindate).diff(dayjs(b.logindate));
      case 'login_desc':
        return dayjs(b.logindate).diff(dayjs(a.logindate));
      case 'initdate_asc':
        return dayjs(a.initdate).diff(dayjs(b.initdate));
      case 'initdate_desc':
        return dayjs(b.initdate).diff(dayjs(a.initdate));
      default:
        return dayjs(b.expiredate).diff(dayjs(a.expiredate)); // 기본값: 유효기간 Down
    }
  });

  // 필터링된 드라이버 목록
  const sortedDrivers = filteredDrivers;

  // 지역(=appname) 필터 드롭다운 옵션
  const appnameOptions = isLocalManager && userAppname ? [userAppname] : Array.from(new Set(drivers.map(driver => driver.appname).filter(Boolean)));

  // 2. 지역 선택 시 추천인 필터 초기화
  const handleRegionChange = (e: SelectChangeEvent<string>) => {
    dispatch(setFilters({ ...filters, region: e.target.value, referealcode: '' }));
    // 연장요청 필터도 초기화
    setShowOnlyExtensionRequests(false);
  };

  // 3. 추천인 필터 선택 시
  const handleReferealChange = (e: SelectChangeEvent<string>) => {
    dispatch(setFilters({ ...filters, referealcode: e.target.value }));
    // 연장요청 필터도 초기화
    setShowOnlyExtensionRequests(false);
  };

  // 연장요청 필터 토글 핸들러
  const handleExtensionRequestFilter = () => {
    setShowOnlyExtensionRequests(!showOnlyExtensionRequests);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* 검색바 */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <TextField
              fullWidth
              label="기사 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch(searchQuery);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleSearch(searchQuery)}
                      sx={{ minWidth: 36, p: 1 }}
                    >
                      <SearchIcon fontSize="small" />
                    </Button>
                  </InputAdornment>
                )
              }}
            />
          </Paper>
        </Grid>

        {/* 필터 패널 */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>지역</InputLabel>
                  <Select
                    value={filters.region || ''}
                    label="지역"
                    onChange={handleRegionChange}
                    disabled={userGrade === 'localmaster' || userGrade === 'localmanager'}
                  >
                    {!isLocalManager && <MenuItem value="">전체</MenuItem>}
                    {appnameOptions.map((appname) => (
                      <MenuItem key={appname} value={appname}>
                        {getDisplayAppName(appname)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>추천인</InputLabel>
                  <Select
                    value={filters.referealcode || ''}
                    label="추천인"
                    onChange={handleReferealChange}
                    disabled={userGrade === 'localmanager'}
                  >
                    <MenuItem value="">전체</MenuItem>
                    {referealOptions.map((ref) => (
                      <MenuItem key={ref} value={ref}>
                        {formatPhoneNumberOrText(ref || '')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    정렬
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label="이름순"
                      onClick={() => handleSort('name')}
                      color={sort === 'name' ? 'primary' : 'default'}
                      variant={sort === 'name' ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip
                      label="전화번호순"
                      onClick={() => handleSort('phone')}
                      color={sort === 'phone' ? 'primary' : 'default'}
                      variant={sort === 'phone' ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip
                      icon={<KeyboardArrowUpIcon />}
                      label="유효일"
                      onClick={() => handleSort('expire_asc')}
                      color={sort === 'expire_asc' ? 'primary' : 'default'}
                      variant={sort === 'expire_asc' ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip
                      icon={<KeyboardArrowDownIcon />}
                      label="유효일"
                      onClick={() => handleSort('expire_desc')}
                      color={sort === 'expire_desc' ? 'primary' : 'default'}
                      variant={sort === 'expire_desc' ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip
                      icon={<KeyboardArrowUpIcon />}
                      label="로그인"
                      onClick={() => handleSort('login_asc')}
                      color={sort === 'login_asc' ? 'primary' : 'default'}
                      variant={sort === 'login_asc' ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip
                      icon={<KeyboardArrowDownIcon />}
                      label="로그인"
                      onClick={() => handleSort('login_desc')}
                      color={sort === 'login_desc' ? 'primary' : 'default'}
                      variant={sort === 'login_desc' ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip
                      icon={<KeyboardArrowDownIcon />}
                      label="설치일(최신)"
                      onClick={() => handleSort('initdate_desc')}
                      color={sort === 'initdate_desc' ? 'primary' : 'default'}
                      variant={sort === 'initdate_desc' ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip
                      icon={<KeyboardArrowUpIcon />}
                      label="설치일(과거)"
                      onClick={() => handleSort('initdate_asc')}
                      color={sort === 'initdate_asc' ? 'primary' : 'default'}
                      variant={sort === 'initdate_asc' ? 'filled' : 'outlined'}
                      size="small"
                    />
                    {canProcessExtensionRequest && (
                      <Chip
                        label="연장요청"
                        onClick={handleExtensionRequestFilter}
                        color={showOnlyExtensionRequests ? 'warning' : 'default'}
                        variant={showOnlyExtensionRequests ? 'filled' : 'outlined'}
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 기사 목록 */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <Grid container spacing={2}>
                {sortedDrivers.length === 0 ? (
                  <Grid item xs={12}>
                    <Typography align="center" color="text.secondary">기사 데이터가 없습니다.</Typography>
                  </Grid>
                ) : (
                  sortedDrivers.map((driver, index) => (
                    <Grid item xs={12} sm={6} md={4} key={driver.id}>
                      <Card sx={{ mb: 2, borderRadius: 3, boxShadow: 3, bgcolor: '#fff', cursor: 'pointer', '&:hover': { boxShadow: 6, bgcolor: '#f0f4ff' } }}>
                        <CardContent>
                          {/* 첫 번째 줄: 기사 이름과 차단 Chip */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', flex: 1 }} onClick={() => handleDriverClick(driver)}>
                              {`${index + 1}) ${driver.username}`}
                            </Typography>
                            {driver.block === 'Y' && (
                              <Chip label="차단" color="error" size="small" sx={{ ml: 1 }} />
                            )}
                          </Box>
                          
                          {/* 두 번째 줄: 추가폰 Chip과 연장요청 버튼들 */}
                          {(userGrade === 'supermaster' && phoneGroups.get(driver.phonenumber) && phoneGroups.get(driver.phonenumber)! > 0) || 
                           (canProcessExtensionRequest && driversWithExtensionRequest.has(driver.id)) ? (
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              mb: 1,
                              minHeight: 32
                            }}>
                              {/* 왼쪽: 추가폰 표시 (SuperMaster만) */}
                              <Box>
                                {userGrade === 'supermaster' && phoneGroups.get(driver.phonenumber) && phoneGroups.get(driver.phonenumber)! > 0 && (
                                  <Chip 
                                    icon={<GroupIcon />}
                                    label={`추가폰 ${phoneGroups.get(driver.phonenumber)}`} 
                                    color="info" 
                                    size="small" 
                                  />
                                )}
                              </Box>
                              
                              {/* 오른쪽: 연장요청 버튼들 */}
                              {canProcessExtensionRequest && driversWithExtensionRequest.has(driver.id) && (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {/* 요청취소 버튼 (오토콜 앱만) */}
                                  {canCancelRequest && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelRequest(driver);
                                      }}
                                      sx={{ 
                                        fontSize: '0.72rem', 
                                        minWidth: 'auto',
                                        padding: '3px 8px',
                                        height: 26
                                      }}
                                    >
                                      요청취소
                                    </Button>
                                  )}
                                  {/* 연장요청 버튼 */}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<ExtensionIcon sx={{ fontSize: '0.9rem' }} />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleExtensionRequest(driver);
                                    }}
                                    sx={{ 
                                      fontSize: '0.72rem', 
                                      minWidth: 'auto',
                                      padding: '3px 8px',
                                      height: 26
                                    }}
                                  >
                                    연장요청
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          ) : null}
                          <Typography variant="body2" color="text.secondary">
                            전화번호: {formatPhoneNumber(driver.phonenumber)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            만료일: <span style={{ color: '#d32f2f', fontWeight: 600 }}>{driver.expiredate}</span>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            설치일: {driver.initdate}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="body2">로그인: {driver.logindate}</Typography>
                            <Typography variant="body2">매크로: {driver.macroapp}</Typography>
                            <Typography variant="body2">폰: {driver.phonemodel}</Typography>
                            <Typography variant="body2">추천: {formatPhoneNumberOrText(driver.referealcode)}</Typography>
                            <Typography variant="body2">앱: {getDisplayAppName(driver.appname)}</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                )}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* 연장승인 다이얼로그 */}
      <Dialog open={extensionDialogOpen} onClose={() => setExtensionDialogOpen(false)}>
        <DialogTitle>연장요청 승인</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{selectedDriver?.username}</strong> 기사의 연장요청을 승인하시겠습니까?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            현재 만료일: {selectedDriver?.expiredate}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            연장 후 만료일: {selectedDriver ? (() => {
              const today = dayjs().startOf('day');
              const currentExpireDate = dayjs(selectedDriver.expiredate).startOf('day');
              
              if (today.isSame(currentExpireDate) || today.isBefore(currentExpireDate)) {
                return dayjs(selectedDriver.expiredate).add(1, 'month').format('YYYY-MM-DD');
              } else {
                return today.add(1, 'month').format('YYYY-MM-DD');
              }
            })() : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtensionDialogOpen(false)}>취소</Button>
          <Button onClick={handleExtensionApproval} variant="contained" color="primary">
            확인
          </Button>
        </DialogActions>
      </Dialog>

      {/* 예치금 부족 다이얼로그 */}
      <Dialog open={insufficientDepositDialogOpen} onClose={() => setInsufficientDepositDialogOpen(false)}>
        <DialogTitle>예치금 부족</DialogTitle>
        <DialogContent>
          <Typography>
            현재 예치금이 20,000원 미만입니다. 연장요청을 진행하시려면 예치금을 충전해야 합니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInsufficientDepositDialogOpen(false)}>확인</Button>
        </DialogActions>
      </Dialog>

      {/* 충청 연장요청 다이얼로그 */}
      <ChungcheongExtensionRequest
        open={chungcheongExtensionDialogOpen}
        onClose={() => setChungcheongExtensionDialogOpen(false)}
        selectedDriver={selectedDriver}
        currentUser={authUser}
        onSuccess={handleChungcheongExtensionSuccess}
      />

      {/* 요청취소 확인 다이얼로그 */}
      <Dialog open={cancelRequestDialogOpen} onClose={() => setCancelRequestDialogOpen(false)}>
        <DialogTitle>요청취소 확인</DialogTitle>
        <DialogContent>
          <Typography>
            요청취소를 진행하시겠습니까?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            기사명: {driverToCancel?.username}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            전화번호: {formatPhoneNumber(driverToCancel?.phonenumber || '')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelRequestDialogOpen(false)}>취소</Button>
          <Button onClick={handleCancelRequestConfirm} variant="contained" color="error">
            확인
          </Button>
        </DialogActions>
      </Dialog>

      {/* 권한 없음 다이얼로그 */}
      <Dialog open={noPermissionDialogOpen} onClose={() => setNoPermissionDialogOpen(false)}>
        <DialogTitle>권한 없음</DialogTitle>
        <DialogContent>
          <Typography>
            권한이 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoPermissionDialogOpen(false)} variant="contained">
            확인
          </Button>
        </DialogActions>
      </Dialog>

      {/* 연장승인 안내 다이얼로그 (localmanager 오토콜 전용) */}
      <Dialog open={extensionGuideDialogOpen} onClose={() => setExtensionGuideDialogOpen(false)}>
        <DialogTitle>연장승인 안내</DialogTitle>
        <DialogContent>
          <Typography component="div" sx={{ whiteSpace: 'pre-line' }}>
            {`연장 승인 문자로 요청
문자→050-5962-5962
문자로→${selectedDriver?.username || '이름'}/${selectedDriver?.phonenumber || '폰_번호'}`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              // 모바일 기기 체크
              const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
              
              if (isMobile && selectedDriver) {
                // SMS 전송 (모바일)
                const phoneNumber = '05059625962';
                const message = `연장요청 : ${selectedDriver.username}/${selectedDriver.phonenumber}`;
                const smsUrl = `sms:${phoneNumber}?body=${encodeURIComponent(message)}`;
                window.location.href = smsUrl;
              }
              
              setExtensionGuideDialogOpen(false);
            }} 
            variant="contained"
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DriversPage; 